import AppHeader from "../components/ds/AppHeader";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import Modal from "../components/ds/Modal";
import SearchField from "../components/ds/SearchField";
import SegmentedControl from "../components/ds/SegmentedControl";
import ViewTabs from "../components/ds/ViewTabs";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "../context/TranslationContext";
import { getMyCampaigns } from "../api/campaigns";
import { SELECTED_TAB } from "../components/campaign/shared";
import { CampaignTile } from "../components/discover/DiscoverAll";
import { formatCampaignMoney, getCampaignDetailsPath } from "../utils/campaignCardUtils";
import { feedMutedTextClass, feedInputClass } from "../styles/feedSurface";
import { billingCampaignGridShellClass } from "../styles/mintlyBillingChrome";

/** Figma V6.0: what a campaign has done so far, under its card. */
const CampaignFigures = ({ campaign, t }) => {
  const stats = campaign.submissionStats;
  const budget = Number(campaign.budget) || 0;
  const remaining = Math.max(budget - (Number(campaign.spent) || 0), 0);
  const figures = [
    [t("home.approval"), stats?.total > 0 ? `${Math.round(stats.approvalRate)}%` : "—", "text-accent-text"],
    [t("campaignFlow.statViews"), (campaign.viewCount || 0).toLocaleString(), "text-label-primary"],
    [t("campaignFlow.statCreators"), (campaign.creatorCount || 0).toLocaleString(), "text-label-primary"],
  ];
  return (
    <div className="mt-auto flex flex-col gap-space-8">
      <div className="grid grid-cols-3 gap-space-12 border-t border-separator-default pt-space-12">
        {figures.map(([label, value, tone]) => (
          <div key={label} className="flex min-w-0 flex-col gap-[2px]">
            <p className="truncate text-caps-label uppercase text-label-tertiary">{label}</p>
            <p className={`truncate text-footnote-semibold ${tone}`}>{value}</p>
          </div>
        ))}
      </div>
      {budget > 0 && (
        <p className="text-caps-label uppercase text-label-tertiary">
          {t("campaignFlow.remainingBudget", { amount: `${formatCampaignMoney(remaining)} ₾` })}
        </p>
      )}
    </div>
  );
};

const MyCampaignsPage = ({ isDark, setToast, spaceId, isOwner = true, canCharge = false, onChargeForAccess }) => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedCampaignId = searchParams.get("campaign");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const mutedText = feedMutedTextClass(isDark);
  const inputClass = feedInputClass(isDark);
  const gridShell = billingCampaignGridShellClass(isDark);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    // Reset campaigns when spaceId changes
    setCampaigns([]);
    
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        // If not owner, view all campaigns for this mint space
        const response = await getMyCampaigns(debouncedSearchQuery, spaceId, !isOwner);
        if (response && response.success) {
          let campaignsData = response.data || [];
          
          // If a campaign is selected, move it to the front
          if (selectedCampaignId) {
            const selectedIndex = campaignsData.findIndex(c => c._id === selectedCampaignId);
            if (selectedIndex > -1) {
              const selectedCampaign = campaignsData[selectedIndex];
              campaignsData = [selectedCampaign, ...campaignsData.filter((_, i) => i !== selectedIndex)];
            }
          }
          
          setCampaigns(campaignsData);
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        if (setToast) {
          setToast(t("campaigns.failedToLoadCampaigns"));
        }
      } finally {
        setLoading(false);
      }
    };

    if (spaceId) {
      fetchCampaigns();
    }
  }, [debouncedSearchQuery, spaceId, isOwner, selectedCampaignId, setToast, t]);

  const STATUS_FILTERS = ["all", "active", "completed", "paused"];
  const statusOptions = STATUS_FILTERS.map((s) => ({
    value: s,
    label: s === "all" ? t("common.all") : t(`common.status${s.charAt(0).toUpperCase() + s.slice(1)}`),
  }));
  const visibleCampaigns =
    statusFilter === "all"
      ? campaigns
      : campaigns.filter((c) => c.status === statusFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={`text-lg ${mutedText}`}>{t("campaigns.loadingCampaigns")}</div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-space-24">
      <AppHeader
        app="creator-rewards"
        eyebrow={t("navigation.creatorRewards")}
        title={t("navigation.creatorRewards")}
        subtitle={t("campaigns.manageCampaigns")}
        action={
          <>
            {isOwner && (
              <Button size="m" onClick={() => navigate(`/create-campaign?spaceId=${spaceId}`)}>
                {t("campaigns.createCampaign")}
              </Button>
            )}
            {canCharge && onChargeForAccess && (
              <Button size="m" variant="gray" icon={<Icon name="wallet" size={20} />} onClick={() => onChargeForAccess?.()}>
                {t("mintSpace.chargeForAccess") || "Create product"}
              </Button>
            )}
          </>
        }
      />
      <button
        type="button"
        onClick={() => setShowInfoModal(true)}
        className="inline-flex items-center gap-space-4 self-start text-caps-button-s uppercase text-app-creator-rewards-soft"
      >
        <Icon name="info" size={16} />
        {t("mintSpace.whatIsCreatorRewards")}
      </button>

      {/* Campaigns List or Empty State */}
      {campaigns.length === 0 && !debouncedSearchQuery ? (
        <EmptyState
          icon="gift"
          title={t("campaigns.noActiveCampaigns")}
          message={isOwner ? t("campaigns.readyToRewardCreators") : t("campaigns.noCampaignsAvailable")}
        />
      ) : (
        <div className="flex w-full flex-col gap-space-24">
          {campaigns.length > 0 && (
            <>
              <div className="flex flex-col gap-space-12 lg:flex-row lg:items-center">
                <SearchField
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("campaigns.searchYourCampaigns")}
                  className="!h-[40px] min-w-0 flex-1"
                />
                {/* Four Georgian labels don't fit a phone's width, so phones get the sideways chips. */}
                <div className="min-[600px]:hidden">
                  <ViewTabs
                    label={t("navigation.creatorRewards")}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    selectedClass={SELECTED_TAB}
                    options={statusOptions}
                  />
                </div>
                <SegmentedControl
                  track="material"
                  label={t("navigation.creatorRewards")}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  selectedClass={SELECTED_TAB}
                  className="hidden shrink-0 min-[600px]:flex [&>button]:flex-auto [&>button]:px-space-8 lg:w-[500px]"
                  options={statusOptions}
                />
              </div>
            </>
          )}

          {/* Campaigns Grid */}
          {campaigns.length === 0 && debouncedSearchQuery ? (
            <div className={`text-center py-12 ${mutedText}`}>
              <p className="text-lg mb-2">{t("campaigns.noCampaigns")}</p>
              <p className="text-subheadline">{t("campaigns.tryAdjustingSearch")}</p>
            </div>
          ) : visibleCampaigns.length === 0 ? (
            <div className={`text-center py-12 ${mutedText}`}>
              <p className="text-lg mb-2">{t("campaigns.noCampaigns")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-space-24 min-[700px]:grid-cols-2 min-[1200px]:grid-cols-3">
              {visibleCampaigns.map((campaign) => (
                <CampaignTile
                  key={campaign._id}
                  campaign={campaign}
                  isSelected={String(campaign._id) === String(selectedCampaignId)}
                  onOpen={(item) => navigate(getCampaignDetailsPath(item))}
                  showJoin={false}
                  footer={<CampaignFigures campaign={campaign} t={t} />}
                  language={language}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        title={t("campaigns.campaignInformation")}
        closeLabel={t("common.close")}
        isDark={isDark}
      >
        <div className="flex flex-col gap-space-16">
          {[
            ["campaigns.cpm", "campaigns.cpmDescription"],
            ["campaigns.budget", "campaigns.budgetDescription"],
            ["campaigns.progress", "campaigns.progressDescription"],
            ["common.status", "campaigns.statusDescription"],
          ].map(([label, text]) => (
            <div key={label} className="flex flex-col gap-space-4">
              <p className="text-subheadline-semibold text-label-primary">{t(label)}</p>
              <p className="text-subheadline text-label-secondary">{t(text)}</p>
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <Button onClick={() => setShowInfoModal(false)}>{t("common.close")}</Button>
        </div>
      </Modal>
      </div>
    </>
  );
};

export default MyCampaignsPage;

